import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';
import type { IUseCase } from '@/shared/contracts/use-case.contract';
import {
  atendimentoEndpoint,
  type AtendimentoPedidoResposta,
  type CancelamentoInfo,
  type CancelarVendaInput,
  type FinalizarVendaInput,
} from '../types';

type Input = { slug: string; orderId: string } & FinalizarVendaInput;

// Dá baixa numa venda salva (assistido): pagamentos, impressão e NFC-e.
export class FinalizarPedidoAtendimentoUseCase implements IUseCase<
  Input,
  AtendimentoPedidoResposta
> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute({ slug, orderId, ...body }: Input): Promise<AtendimentoPedidoResposta> {
    const response = await this.client.sendRequest<
      { data: AtendimentoPedidoResposta },
      FinalizarVendaInput
    >({
      endpoint: `${atendimentoEndpoint(slug)}/pedidos/${encodeURIComponent(orderId)}/finalizar`,
      method: HttpMethod.POST,
      body,
    });

    return response.data;
  }

  // Atendente conclui o pedido do cliente (QR) já pago — quando entrega. Online
  // (não passa pelo outbox): 422 se o pagamento ainda não caiu.
  async concluir({ slug, orderId }: { slug: string; orderId: string }) {
    const response = await this.client.sendRequest<{ data: AtendimentoPedidoResposta }, never>({
      endpoint: `${atendimentoEndpoint(slug)}/pedidos/${encodeURIComponent(orderId)}/concluir`,
      method: HttpMethod.POST,
    });

    return response.data;
  }

  // PRAT-115: pagamento online e NFC-e da venda, para o aviso antes de cancelar.
  async cancelamento({ slug, orderId }: { slug: string; orderId: string }) {
    const response = await this.client.sendRequest<{ data: CancelamentoInfo }, never>({
      endpoint: `${atendimentoEndpoint(slug)}/pedidos/${encodeURIComponent(orderId)}/cancelamento`,
      method: HttpMethod.GET,
    });

    return response.data;
  }

  // PRAT-115: cancela a venda (operador ou QR). Online, como o concluir: devolve
  // o estoque e cancela a NFC-e no prazo; PIX online exige `pix_refund_ack`.
  async cancelar({
    slug,
    orderId,
    ...body
  }: { slug: string; orderId: string } & CancelarVendaInput) {
    const response = await this.client.sendRequest<
      { data: AtendimentoPedidoResposta },
      CancelarVendaInput
    >({
      endpoint: `${atendimentoEndpoint(slug)}/pedidos/${encodeURIComponent(orderId)}/cancelar`,
      method: HttpMethod.POST,
      body,
    });

    return response.data;
  }
}
